import { useMemo } from "react";

import { FetcherResult } from "../api-client/fetcher";
import { ModifiedSWRResponse } from "../api-client/useSwrGet";
import { LanguageMap } from "../i18n/useLanguage";
import {
  VehicleInfoByChassisPayload,
  useGetVehicleInfoByChassis,
} from "./useGetVehicleInfoByChassis";
import {
  PartListSummary,
  VehicleInfoByVehicleKeysPayload,
  useGetVehicleInfoByVehicleKeys,
} from "./useGetVehicleInfoByVehicleKeys";

export const useGetVehicleInfo = (): HookResult => {
  const { data: keys, ...restKeys } = useGetVehicleInfoByVehicleKeys();
  const { data: chassis, ...restChassis } = useGetVehicleInfoByChassis();

  const data = keys ?? chassis;

  const enhancedData = useMemo(() => {
    if (!data) {
      return;
    }
    const vehicleFullTireSizeInfo = data?.vehicleFullTireSizeInfo.sort(
      (a, b) => Number(a.rimDiameter) - Number(b.rimDiameter)
    );
    const defaultVehicleSubmodelTiresizeId =
      data.vehicleFullTireSizeInfo[0]?.vehicleSubmodelTiresizeId || "";
    return {
      ...data,
      defaultVehicleSubmodelTiresizeId,
      vehicleFullTireSizeInfo,
    };
  }, [data]);

  if (!enhancedData) {
    return {
      ...restKeys,
      data: undefined,
    } as HookResult;
  }

  if (keys) {
    return {
      ...restKeys,
      data: enhancedData,
    } as HookResult;
  }

  return {
    ...restChassis,
    data: enhancedData,
  };
};

type HookResult = ModifiedSWRResponse<FetcherResult<Payload>, VehicleInfo>;

type Payload = VehicleInfoByChassisPayload | VehicleInfoByVehicleKeysPayload;

export type VehicleInfo = VehicleInfoByChassisPayload & {
  defaultVehicleSubmodelTiresizeId: string;
  partListSummary?: PartListSummary;
  vehicleManufacturerName?: string;
  vehicleModelId?: string;
  vehicleModelName?: LanguageMap;
  vehicleSubmodelName?: LanguageMap;
  vehicleVisualizerForegroundImageUrl?: string;
  vehicleVisualizerShadowImageUrl?: string;
  vehicleYear?: string;
};
